import { useI18nStore } from '../../shared/i18n/i18nStore';
import { columnSums } from './roundDigits';

/**
 * 三个区块 → 十位公共数字。B 行 → 列相加 → 只留个位 → D₁..D₁₀。
 *
 * <p>与游戏无关：房间三玩法和两档 Milhar 读的是同一批轮次、同一套 D₁..D₁₀，
 * 差别只在**从这十位里取哪一段**。所以这张表抽出来共用，算法调整只改一处。
 *
 * @param highlight 1-based 闭区间。给出时高亮那一段 D —— Milhar 用它标出本档
 *   截取的窗口；房间玩法不传，整行等权。
 */
export function RoundDigitsTable({ blockDigits, digits, highlight = null }: {
  blockDigits: string[];
  digits: string;
  highlight?: [number, number] | null;
}) {
  const t = useI18nStore((state) => state.t);
  const sums = columnSums(blockDigits);
  const on = (position: number) =>
    !!highlight && position >= highlight[0] && position <= highlight[1];

  if (blockDigits.length === 0 || sums.length !== 10) {
    return (
      <div className="draw-digit-grid-react" data-draw-digit-grid-react="true">
        {(digits || '----------').slice(0, 10).split('').map((digit, index) => (
          <b key={`draw-d-${index}`}>
            <small>D{index + 1}</small>
            {digit === '-' ? '-' : digit}
          </b>
        ))}
      </div>
    );
  }

  return (
    <div className="rules-d-table" data-draw-block-digits-react="true">
      {blockDigits.slice(0, 3).map((row, rowIndex) => (
        <div className={`rules-d-row rules-d-row-b${rowIndex + 1}`} key={`digits-b-${rowIndex}`}>
          <span className="rules-d-label">B{rowIndex + 1}</span>
          {row.padEnd(10, '-').slice(0, 10).split('').map((digit, digitIndex) => (
            <span className="rules-d-cell" key={`digits-b-${rowIndex}-${digitIndex}`}>{digit}</span>
          ))}
        </div>
      ))}
      <div className="rules-d-row rules-d-row-sum">
        <span className="rules-d-label">{t('rulesPart2SumLabel')}</span>
        {sums.map((value, index) => (
          <span
            className={`rules-d-cell rules-d-cell-sum${value > 9 ? ' rules-d-cell-overflow' : ''}`}
            key={`digits-sum-${index}`}
          >{value}</span>
        ))}
      </div>
      <div className="rules-d-fold-hint">{t('rulesPart2FoldHint')}</div>
      <div className="rules-d-row">
        <span className="rules-d-label">{t('rulesPart2ModLabel')}</span>
        {(digits || '----------').slice(0, 10).split('').map((digit, index) => (
          <span
            className={`rules-d-cell rules-d-cell-result${on(index + 1) ? ' is-window' : ''}`}
            key={`digits-d-${index}`}
          >{digit}</span>
        ))}
      </div>
      <div className="rules-d-row rules-d-row-dlabels">
        <span className="rules-d-label">&nbsp;</span>
        {Array.from({ length: 10 }, (_, i) => (
          <span
            key={i}
            className={`rules-d-col-label rules-d-dlabel${on(i + 1) ? ' is-window' : ''}`}
          >{i < 9 ? `D${'₁₂₃₄₅₆₇₈₉'[i]}` : 'D₁₀'}</span>
        ))}
      </div>
    </div>
  );
}
